<script>
/**
 * @component ValidationHistory
 * @description Historial de validaciones de prestaciones.
 * Doctor: ve solo las suyas con total valorizado del mes.
 * Admin/operador: ve todas, puede filtrar por prestador y período.
 */
import validationService from "@/services/validation";
import { mapGetters } from "vuex";

const MONTHS = [
  "",
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const REJECTION_LABELS = {
  member_inactive: "Afiliado inactivo",
  member_no_plan: "Sin plan activo",
  invalid_token: "Token inválido",
  practice_not_covered: "Prestación no cubierta",
  provider_inactive: "Prestador inactivo",
  provider_no_contract: "Sin contrato vigente",
};

export default {
  name: "ValidationHistory",
  computed: {
    ...mapGetters(["isAdmin"]),
    /** @returns {string} Label del período seleccionado */
    periodLabel() {
      return `${MONTHS[this.filterMonth]} ${this.filterYear}`;
    },
    /** @returns {number} Total valorizado de las validaciones aprobadas visibles */
    totalApproved() {
      return this.validations
        .filter((v) => v.status === "approved")
        .reduce((sum, v) => sum + (v.valorization?.amount || 0), 0);
    },
  },
  data() {
    const now = new Date();
    return {
      validations: [],
      total: 0,
      page: 1,
      pageSize: 20,
      loading: false,
      filterStatus: "",
      filterYear: now.getFullYear(),
      filterMonth: now.getMonth() + 1,
      yearOptions: [now.getFullYear(), now.getFullYear() - 1],
      monthOptions: MONTHS.slice(1).map((name, i) => ({
        label: name,
        value: i + 1,
      })),
    };
  },
  created() {
    this.fetchValidations();
  },
  methods: {
    /**
     * @method fetchValidations
     * @desc Carga el historial con los filtros activos
     */
    async fetchValidations() {
      this.loading = true;
      try {
        const params = {
          offset: (this.page - 1) * this.pageSize,
          limit: this.pageSize,
          year: this.filterYear,
          month: this.filterMonth,
        };
        if (this.filterStatus) params.status = this.filterStatus;
        const res = await validationService.get(params);
        this.validations = res.docs || [];
        this.total = res.total || 0;
      } catch {
        this.$message.error("Error al cargar el historial");
      } finally {
        this.loading = false;
      }
    },

    /** @method onFilterChange */
    onFilterChange() {
      this.page = 1;
      this.fetchValidations();
    },

    /** @method onPageChange */
    onPageChange(page) {
      this.page = page;
      this.fetchValidations();
    },

    /** @method formatDate */
    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },

    /** @method formatCurrency */
    formatCurrency(n) {
      return n != null ? `$${Number(n).toLocaleString("es-AR")}` : "-";
    },

    /**
     * @method getRejectionText
     * @param {string[]} reasons
     * @returns {string}
     */
    getRejectionText(reasons) {
      if (!reasons || !reasons.length) return "-";
      return reasons.map((r) => REJECTION_LABELS[r] || r).join(", ");
    },
  },
};
</script>

<template lang="pug">
  div
    //- Filtros
    .history-toolbar
      .history-toolbar__filters
        el-select(
          v-model="filterYear"
          size="small"
          style="width:100px"
          @change="onFilterChange"
        )
          el-option(v-for="y in yearOptions" :key="y" :label="y" :value="y")
        el-select(
          v-model="filterMonth"
          size="small"
          style="width:130px"
          @change="onFilterChange"
        )
          el-option(
            v-for="m in monthOptions"
            :key="m.value"
            :label="m.label"
            :value="m.value"
          )
        el-select(
          v-model="filterStatus"
          placeholder="Todos los estados"
          clearable size="small"
          style="width:180px"
          @change="onFilterChange"
        )
          el-option(label="Autorizadas" value="approved")
          el-option(label="Rechazadas" value="rejected")

      //- Resumen del período
      .history-toolbar__summary(v-if="validations.length")
        span.summary-label {{ periodLabel }}
        span.summary-divider ·
        span.summary-count {{ total }} validaciones
        span.summary-divider ·
        span.summary-amount {{ formatCurrency(totalApproved) }} autorizado

    //- Tabla
    el-table(
      :data="validations"
      v-loading="loading"
      border
      size="small"
      style="margin-top:16px"
    )
      el-table-column(label="Fecha" width="150")
        template(slot-scope="{ row }") {{ formatDate(row.createdAt) }}

      el-table-column(label="TRX" width="155")
        template(slot-scope="{ row }")
          span.cell-trx(v-if="row.transactionCode") {{ row.transactionCode }}
          span(v-else style="color:#c0c4cc") —

      el-table-column(label="Origen" width="160")
        template(slot-scope="{ row }")
          span.cell-origin(v-if="row._sourceType === 'authorization'") Autorización
          span.cell-origin.cell-origin--direct(v-else) Validación directa

      el-table-column(label="Afiliado")
        template(slot-scope="{ row }")
          div(v-if="row.memberSnapshot")
            span.cell-primary {{ row.memberSnapshot.lastName }}, {{ row.memberSnapshot.firstName }}
            span.cell-secondary  · {{ row.memberSnapshot.planName || '-' }}
          span(v-else) -

      el-table-column(label="Prestación" show-overflow-tooltip)
        template(slot-scope="{ row }")
          div(v-if="row.practiceSnapshot")
            span.cell-code {{ row.practiceSnapshot.code }}
            span.cell-secondary  {{ row.practiceSnapshot.name }}
          span(v-else) -

      el-table-column(label="Prestador" v-if="isAdmin")
        template(slot-scope="{ row }")
          span(v-if="row.providerSnapshot")
            | {{ row.providerSnapshot.lastName }}, {{ row.providerSnapshot.firstName }}
          span(v-else) -

      el-table-column(label="Estado" width="130")
        template(slot-scope="{ row }")
          el-tag(
            :type="row.status === 'approved' ? 'success' : 'danger'"
            size="small"
          )
            | {{ row.status === 'approved' ? 'Autorizada' : 'Rechazada' }}

      el-table-column(label="Valor / Motivo" width="200")
        template(slot-scope="{ row }")
          div(v-if="row.status === 'approved'")
            span.cell-amount {{ formatCurrency(row.valorization && row.valorization.amount) }}
            span.cell-copago(v-if="row.valorization && row.valorization.hasCopago")
              |  · Copago {{ formatCurrency(row.valorization.copagoAmount) }}
          div.cell-rejection(v-else) {{ getRejectionText(row.rejectionReasons) }}

    //- Paginación
    .pagination-wrapper
      el-pagination(
        v-if="total > pageSize"
        layout="prev, pager, next, total"
        :total="total"
        :page-size="pageSize"
        :current-page="page"
        @current-change="onPageChange"
      )

    //- Vacío
    .empty-state(v-if="!loading && validations.length === 0")
      i.el-icon-document
      p No hay validaciones para el período seleccionado
</template>

<style lang="scss" scoped>
.history-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;

  &__filters {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__summary {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
  }
}

.summary-label {
  font-weight: 700;
  color: #002d5e;
}
.summary-divider {
  color: #cbd5e1;
}
.summary-count {
  color: #6b7294;
}
.summary-amount {
  font-weight: 700;
  color: #059669;
}

.cell-primary {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #002d5e;
}
.cell-secondary {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  color: #6b7294;
}
.cell-code {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #002d5e;
  margin-right: 4px;
}
.cell-amount {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #059669;
}
.cell-copago {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 11px;
  color: #6b7294;
}
.cell-origin {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: #eeedfe;
  color: #26215c;
}
.cell-origin--direct {
  background: #e1f5ee;
  color: #085041;
}
.cell-trx {
  font-family: monospace;
  font-size: 12px;
  font-weight: 700;
  color: #002d5e;
}
.cell-rejection {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  color: #dc2626;
}

.pagination-wrapper {
  margin-top: 16px;
  text-align: center;
}

.empty-state {
  text-align: center;
  padding: 48px 0;
  color: #94a3b8;
  i {
    font-size: 40px;
    display: block;
    margin-bottom: 12px;
  }
  p {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    margin: 0;
  }
}
</style>
